import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Leaf, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { coachingFileUrl } from '../hooks/useCoachingResources';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FileView = ({ r }) => {
  const url = coachingFileUrl(r.id);
  const ct = r.content_type || '';
  if (ct.includes('pdf')) return <iframe title={r.title} src={url} className="w-full h-[80vh] rounded-xl border border-stone-200 bg-white" data-testid="coaching-file-pdf" />;
  if (ct.startsWith('image/')) return <img src={url} alt={r.title} className="w-full rounded-xl border border-stone-200" data-testid="coaching-file-image" />;
  if (ct.startsWith('video/')) return <video src={url} controls playsInline className="w-full rounded-xl bg-black" data-testid="coaching-file-video" />;
  if (ct.startsWith('audio/')) return <audio src={url} controls className="w-full" data-testid="coaching-file-audio" />;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-forest text-cream font-body text-sm px-5 py-2.5 rounded-full" data-testid="coaching-file-link">
      <ExternalLink size={15} /> Open {r.file_name || 'file'}
    </a>
  );
};

export default function CoachingResourcePage() {
  const { id } = useParams();
  const { user, hasAccess, loading } = useAuth();
  const [r, setR] = useState(null);
  const [error, setError] = useState('');
  const isAdmin = !!sessionStorage.getItem('gw_admin_auth');
  const allowed = isAdmin || (user && hasAccess('vip'));

  useEffect(() => {
    if (!allowed) return;
    fetch(`${API}/coaching-resources/${id}`).then((res) => (res.ok ? res.json() : Promise.reject()))
      .then(setR).catch(() => setError('This resource is no longer available.'));
  }, [id, allowed]);

  return (
    <div className="min-h-screen bg-cream py-12 px-4" data-testid="coaching-resource-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/programs/vip/journal" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-8">
          <ArrowLeft size={16} /> Back to my journal
        </Link>
        {!loading && !allowed ? (
          <p className="font-body text-stone-600" data-testid="coaching-resource-locked">This resource is for V.I.P. members. Please sign in to view it.</p>
        ) : error ? (
          <p className="font-body text-stone-600" data-testid="coaching-resource-error">{error}</p>
        ) : !r ? (
          <div className="flex items-center gap-2 text-stone-500 font-body"><Loader2 size={18} className="animate-spin" /> Loading…</div>
        ) : (
          <article>
            <p className="inline-flex items-center gap-2 text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em] mb-3">
              <Leaf size={14} /> {r.group || 'Nutrition & Wellness Coaching'}
            </p>
            <h1 className="font-heading text-4xl md:text-5xl text-forest mb-6" data-testid="coaching-resource-title">{r.title}</h1>
            {r.body && (
              <div className="font-body text-stone-700 leading-relaxed whitespace-pre-wrap bg-white border border-stone-200 rounded-2xl p-6 md:p-8 mb-8" data-testid="coaching-resource-body">{r.body}</div>
            )}
            {r.has_file && <FileView r={r} />}
            {!r.body && !r.has_file && (
              <p className="font-body text-stone-500 italic" data-testid="coaching-resource-empty">Jill is still putting this one together — check back soon.</p>
            )}
          </article>
        )}
      </div>
    </div>
  );
}
